import { useMutation } from "@apollo/client";
import { Menu, MenuItem } from "@szhsin/react-menu";
import { isTextSelection } from "@tiptap/core";
import { Editor } from "@tiptap/react";
import { delay } from "lodash";
import { useCallback, useRef } from "react";
import {
  RiBold,
  RiCheckboxLine,
  RiFontColor,
  RiFormatClear,
  RiItalic,
  RiLink,
  RiLinkUnlink,
  RiMessage2Line,
  RiPaintBrushLine,
  RiStrikethrough,
} from "react-icons/ri";
import {
  TbColumnInsertLeft,
  TbColumnInsertRight,
  TbFreezeColumn,
  TbFreezeRow,
  TbRowInsertBottom,
  TbRowInsertTop,
  TbTarget,
  TbTrash,
} from "react-icons/tb";
import store from "store2";
import { hideAll } from "tippy.js";
import { DateRangeEnum } from "types/topicflow";
import { v4 as uuidv4 } from "uuid";

import { refetchActionItemsInCollapsibleContainers } from "@apps/action-items-collapsible/helpers";
import getMeetingGoalsQuery from "@apps/meeting-new/graphql/get-meeting-goals-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import createOrUpdateArtifactMutation from "@components/wysiwyg/extensions/artifacts/graphql/create-or-update-artifact-mutation";
import { artifactType } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { dateRangeToDateArray } from "@helpers/helpers";

import { toggleLink } from "../helpers";
import { BubbleMenu } from "./bubble-menu/bubble-menu-component";

const buttonClassName = "bg-white px-1.5 py-1 text-gray-700 hover:bg-gray-100";
const tableButtonClassName = "bg-white px-1.5 py-1 text-gray-500";
const activeButtonClassName = "bg-blue-600 hover:bg-blue-600 text-white";
const menuClassName =
  "flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100";

const highlightColours = [
  { value: "#f3f4f6", label: "Gray" },
  { value: "#fee2e2", label: "Red" },
  { value: "#ffedd5", label: "Orange" },
  { value: "#fef3c7", label: "Yellow" },
  { value: "#d1fae5", label: "Green" },
  { value: "#dbeafe", label: "Blue" },
  { value: "#e0e7ff", label: "Purple" },
  { value: "#ede9fe", label: "Violet" },
  { value: "#ffe4e6", label: "Pink" },
];

export default function CustomBubbleMenu({
  editor,
  enableComment = false,
  disableBubbleMenuArtifactActions = false,
  meetingId,
  organizationId,
  topicId,
  onCreateCommentUuid,
}: {
  editor: Editor;
  enableComment?: boolean;
  disableBubbleMenuArtifactActions?: boolean;
  meetingId?: number;
  organizationId?: number | null;
  topicId?: number | null;
  onCreateCommentUuid?: (uuid: string) => void;
}) {
  const ref = useRef<null | HTMLDivElement>(null);
  const setLink = useCallback(() => {
    toggleLink(editor);
  }, [editor]);

  const currentOrganization = currentOrganizationVar();
  const goalRange = dateRangeToDateArray({
    range: DateRangeEnum.thisQuarter,
    quarterStartMonth: currentOrganization.quarterStartMonth,
  });
  const label = useLabel();

  const [updateArtifact] = useMutation(createOrUpdateArtifactMutation);
  const selectedText = editor.state.doc.textBetween(
    editor.state.selection.from,
    editor.state.selection.to,
    " ",
  );
  const saveArtifact = (dataToSave: any) => {
    const variables = {
      organizationId,
      meetingId,
      topicId,
      uuid: uuidv4(),
      ...dataToSave,
    };
    updateArtifact({
      variables,
      onCompleted: (data) => {
        const artifact = data.createOrUpdateArtifact.artifact;
        if (dataToSave.artifactType === artifactType.actionItem) {
          editor
            .chain()
            .clearNodes()
            .insertActionItem({
              artifactId: artifact.id,
              ...artifact,
            })
            .run();
        } else if (dataToSave.artifactType === artifactType.goal) {
          editor
            .chain()
            .clearNodes()
            .insertGoal({
              artifactId: artifact.id,
              ...artifact,
            })
            .run();
        }
      },

      refetchQueries: [getMeetingGoalsQuery],
      update: (_cache, response) => {
        refetchActionItemsInCollapsibleContainers(
          response.data.createOrUpdateArtifact.artifact,
        );
      },
    });
  };

  const handleHideMenu = (tippy: any) => {
    // for some reason the tooltips on the menu buttons stay opened
    // when the editor text changes and the bubble menu is hidden.
    // The line below forces all tooltip/tippy instances to be closed...
    hideAll(); // can enable this as it hides the
    if (tippy) {
      tippy.setProps({ placement: "top" });
    }
  };

  // Detect if current selection has a comment already
  const { selection, doc } = editor.view.state;
  let selectionContainsACommentPos: null | number = null;
  if (!selection.empty) {
    doc.nodesBetween(
      editor.state.selection.from,
      editor.state.selection.to,
      (node, pos) => {
        if (
          !selectionContainsACommentPos &&
          node.marks.find((mark) => mark.type.name === "comment")
        ) {
          selectionContainsACommentPos = pos + 1;
        }
      },
      0,
    );
  }

  const handleShowCommentForm = () => {
    if (selectionContainsACommentPos !== null) {
      const existingUuid = editor.getAttributes("comment")?.comment || null;
      if (existingUuid) {
        delay(() => {
          if (onCreateCommentUuid) onCreateCommentUuid(existingUuid);
        }, 50);
      }
    } else {
      const uuid = uuidv4();
      editor.chain().setComment(uuid).run();
      delay(() => {
        if (onCreateCommentUuid) onCreateCommentUuid(uuid);
      }, 50);
    }
  };

  // const { doc, selection } = state;
  const { empty, from, to } = selection;

  // Sometime check for `empty` is not enough.
  // Doubleclick an empty paragraph returns a node size of 2.
  // So we check also for an empty text size.
  const isEmptyTextBlock =
    !doc.textBetween(from, to).length &&
    isTextSelection(editor.view.state.selection);

  const lastHighlight = store("editor:last-highlight-color");

  const NodeMenuItems = (
    <Menu
      align="start"
      transition={false}
      className="text-sm not-prose z-dropdown"
      menuButton={
        <button
          className={classNames(
            buttonClassName,
            "text-sm tracking-tight flex items-center gap-0.5",
          )}
          aria-label="Button node text"
        >
          {editor.isActive("orderedList")
            ? "Numbered list"
            : editor.isActive("bulletList")
              ? "Bullet list"
              : editor.isActive("codeBlock")
                ? "Code block"
                : editor.isActive("blockquote")
                  ? "Quote"
                  : "Paragraph"}
          <TopicflowIcon type="chevronDown" className="h-4 w-4 text-gray-500" />
        </button>
      }
    >
      <MenuItem
        className={classNames(menuClassName, "rounded-t-md")}
        onClick={() => editor.chain().clearNodes().run()}
      >
        Paragraph
      </MenuItem>
      <MenuItem
        className={menuClassName}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        Numbered list
      </MenuItem>
      <MenuItem
        className={menuClassName}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        Bullet list
      </MenuItem>
      <MenuItem
        className={menuClassName}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        Quote
      </MenuItem>
      <MenuItem
        className={classNames(menuClassName, "rounded-b-md")}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        Code
      </MenuItem>
    </Menu>
  );
  const CommentMenuItem = enableComment && (
    <Tooltip text="Add a comment (Cmd+Alt+M)">
      <button
        type="button"
        onClick={handleShowCommentForm}
        className={classNames(
          buttonClassName,
          "flex gap-1 items-center text-sm tracking-tight",
          (editor.isActive("comment") ||
            selectionContainsACommentPos !== null) &&
            activeButtonClassName,
        )}
        aria-label="Button comment text"
      >
        <RiMessage2Line className="shrink-0 h-4 w-4" />
        Comment
      </button>
    </Tooltip>
  );
  const MarkMenuItems = (
    <>
      <Tooltip text="Link (Cmd+K)">
        <button
          type="button"
          onClick={setLink}
          className={classNames(
            buttonClassName,
            editor.isActive("customLink") && activeButtonClassName,
          )}
        >
          {editor.isActive("customLink") ? <RiLinkUnlink /> : <RiLink />}
        </button>
      </Tooltip>
      <Tooltip text="Bold (Cmd+B)">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={classNames(
            buttonClassName,
            editor.isActive("bold") && activeButtonClassName,
          )}
        >
          <RiBold />
        </button>
      </Tooltip>
      <Tooltip text="Italic (Cmd+I)">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={classNames(
            buttonClassName,
            editor.isActive("italic") && activeButtonClassName,
          )}
        >
          <RiItalic />
        </button>
      </Tooltip>

      <Tooltip text="Underline (Cmd+U)">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={classNames(
            buttonClassName,
            editor.isActive("underline") && activeButtonClassName,
          )}
        >
          <RiFontColor />
        </button>
      </Tooltip>

      <Tooltip text="Strikethrough (Cmd+Shift+X)">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={classNames(
            buttonClassName,
            editor.isActive("strike") && activeButtonClassName,
          )}
        >
          <RiStrikethrough />
        </button>
      </Tooltip>
    </>
  );
  const HighlightMenuItem = (
    <Menu
      align="start"
      transition={false}
      className="text-sm not-prose z-dropdown"
      menuButton={
        <button
          className={classNames(
            buttonClassName,
            "text-sm tracking-tight flex items-center gap-0.5",
          )}
          aria-label="Button highlight text"
        >
          <RiPaintBrushLine />
        </button>
      }
    >
      {lastHighlight && (
        <>
          <MenuItem
            className="py-0.5 px-2 text-2xs text-gray-400 uppercase"
            disabled
          >
            Last used
          </MenuItem>
          <MenuItem>
            <button
              className={classNames("flex items-center gap-2 w-full text-left")}
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .setHighlight({ color: lastHighlight.value })
                  .run();
              }}
            >
              <div
                className="h-3 w-3 border"
                style={{ background: lastHighlight.value }}
              />
              {lastHighlight.label}
            </button>
          </MenuItem>
        </>
      )}
      <MenuItem
        className="py-0.5 px-2 text-2xs text-gray-400 uppercase"
        disabled
      >
        Colors
      </MenuItem>
      <MenuItem>
        <button
          className="flex items-center gap-2 w-full text-left"
          onClick={() => {
            editor.chain().focus().unsetHighlight().run();
          }}
        >
          <div className="h-3 w-3 border" style={{ background: "#fff" }} />
          Default
        </button>
      </MenuItem>
      {highlightColours
        .map(({ label, value }) => ({
          label,
          value,
          onClick: () => {
            store("editor:last-highlight-color", { label, value });
            editor.chain().focus().setHighlight({ color: value }).run();
          },
        }))
        .map(({ label, value, onClick }) => (
          <MenuItem key={label}>
            <button
              className="flex items-center gap-2 w-full text-left"
              onClick={onClick}
            >
              <div className="h-3 w-3 border" style={{ background: value }} />
              {label}
            </button>
          </MenuItem>
        ))}
    </Menu>
  );

  const ClearMenuItem = (
    <Tooltip text="Clear formatting">
      <button
        type="button"
        className={classNames(buttonClassName, "last:rounded-r-sm")}
        onClick={() => editor.chain().unsetAllMarks().clearNodes().run()}
      >
        <RiFormatClear />
      </button>
    </Tooltip>
  );
  const ConvertMenuItem = (
    <>
      <Tooltip text="Convert to action item">
        <button
          aria-label="Button convert to action item"
          type="button"
          className={classNames(
            buttonClassName,
            "last:rounded-r-sm text-violet-600",
          )}
          onClick={() => {
            saveArtifact({
              title: selectedText,
              artifactType: artifactType.actionItem,
            });
          }}
        >
          <RiCheckboxLine />
        </button>
      </Tooltip>
      <Tooltip text={`Convert to ${label("goal")}`}>
        <button
          type="button"
          className={classNames(
            buttonClassName,
            "last:rounded-r-sm text-blue-600",
          )}
          onClick={() => {
            saveArtifact({
              title: selectedText,
              artifactType: artifactType.goal,
              additionalFields: {
                startDate: goalRange[0],
                dueDate: goalRange[1],
              },
            });
          }}
        >
          <TbTarget />
        </button>
      </Tooltip>
    </>
  );

  const TableMenuItems = (
    <>
      {editor.commands.addColumnBefore && editor.can().addColumnBefore() && (
        <Tooltip text="Add column before">
          <button
            type="button"
            onClick={() => editor.chain().addColumnBefore().run()}
            className={classNames(tableButtonClassName, "rounded-l-sm")}
          >
            <TbColumnInsertLeft className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
      {editor.commands.addColumnAfter && editor.can().addColumnAfter() && (
        <Tooltip text="Add column after">
          <button
            type="button"
            onClick={() => editor.chain().addColumnAfter().run()}
            className={tableButtonClassName}
          >
            <TbColumnInsertRight className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
      {editor.commands.deleteColumn && editor.can().deleteColumn() && (
        <Tooltip text="Delete column">
          <button
            type="button"
            onClick={() => editor.chain().deleteColumn().run()}
            className={tableButtonClassName}
          >
            <TbFreezeColumn className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
      {editor.commands.addRowBefore && editor.can().addRowBefore() && (
        <Tooltip text="Add row before">
          <button
            type="button"
            onClick={() => editor.chain().addRowBefore().run()}
            className={tableButtonClassName}
          >
            <TbRowInsertTop className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
      {editor.commands.addRowAfter && editor.can().addRowAfter() && (
        <Tooltip text="Add row after">
          <button
            type="button"
            onClick={() => editor.chain().addRowAfter().run()}
            className={tableButtonClassName}
          >
            <TbRowInsertBottom className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
      {editor.commands.deleteRow && editor.can().deleteRow() && (
        <Tooltip text="Delete row">
          <button
            type="button"
            onClick={() => editor.chain().deleteRow().run()}
            className={tableButtonClassName}
          >
            <TbFreezeRow className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
      {editor.commands.mergeCells && editor.can().mergeCells() && (
        <Tooltip text="Merge cells">
          <button
            type="button"
            onClick={() => editor.chain().mergeCells().run()}
            className={classNames(tableButtonClassName, "text-sm")}
          >
            Merge cells
          </button>
        </Tooltip>
      )}
      {editor.commands.splitCell && editor.can().splitCell() && (
        <Tooltip text="Split cell">
          <button
            type="button"
            onClick={() => editor.chain().splitCell().run()}
            className={classNames(tableButtonClassName, "text-sm")}
          >
            Split cell
          </button>
        </Tooltip>
      )}
      {editor.commands.deleteTable && editor.can().deleteTable() && (
        <Tooltip text="Delete table">
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm("Are you sure you want to delete this table?")
              ) {
                editor.chain().deleteTable().run();
              }
            }}
            className={classNames(tableButtonClassName, "text-sm rounded-r-sm")}
          >
            <TbTrash className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
    </>
  );

  return (
    <div ref={ref}>
      <BubbleMenu
        editor={editor}
        tippyOptions={{
          appendTo: (ref2) => ref.current || ref2,
          maxWidth: "none",
          theme: "headless",
          onHidden: handleHideMenu,
        }}
        className="gap-px border bg-gray-200 flex rounded-sm shadow-sm"
        shouldShow={function shouldShowBubbleMenu({
          editor,
          view,
          state,
          from,
          to,
        }) {
          if (
            editor.isActive("explorer") ||
            editor.isActive("decision") ||
            editor.isActive("recognition") ||
            editor.isActive("action_item") ||
            editor.isActive("rating") ||
            editor.isActive("document") ||
            editor.isActive("goal") ||
            editor.isActive("feedback") ||
            editor.isActive("image")
          ) {
            return false;
          }
          const { doc, selection } = state;
          const { empty } = selection;

          // Sometime check for `empty` is not enough.
          // Doubleclick an empty paragraph returns a node size of 2.
          // So we check also for an empty text size.
          const isEmptyTextBlock =
            !doc.textBetween(from, to).length &&
            isTextSelection(state.selection);
          const hasEditorFocus = view.hasFocus();

          if (!hasEditorFocus || empty || isEmptyTextBlock) {
            if (editor.isActive("table")) {
              return true;
            }
            return false;
          }
          return true;
        }}
      >
        {editor.isActive("table") && (isEmptyTextBlock || empty) ? (
          TableMenuItems
        ) : (
          <>
            {NodeMenuItems}
            {CommentMenuItem}
            <div className="flex">
              {MarkMenuItems}
              {HighlightMenuItem}
              {ClearMenuItem}
              {disableBubbleMenuArtifactActions ? null : ConvertMenuItem}
            </div>
          </>
        )}
      </BubbleMenu>
    </div>
  );
}
